import React, { useMemo } from 'react';

import { IPersonaFront } from '@api/routers/user/service';
import styled from '@emotion/styled';
import { Blockchain } from '@repo/prsn-blockchain';
import { useTranslations } from 'next-intl';

import { SharpPersonaImage } from '@web/ui/components/SharpPersonaImage';
import { BcBaseIcon, BcEthIcon } from '@web/ui/icons';

import BoostIcon from '../../assets/bolt.svg';

const PersonaItemContainer = styled.div<{ rotationAngle: number }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-inline: ${({ theme }) => theme.spacing('0250')};
  padding-top: ${({ theme }) => theme.spacing('0250')};
  padding-bottom: 10px;
  background: ${({ theme }) => theme.color.surfaceContainerHighest};
  border-radius: ${({ theme }) => theme.shape.corner['1000']};
  box-shadow: ${({ theme }) => theme.elevation.level1};
  transform: ${({ rotationAngle }) => `rotate(${rotationAngle}deg)`};
  transition: transform 150ms ease;

  :not(:first-child) {
    margin-left: -${({ theme }) => theme.spacing('1000')};
  }

  :hover {
    transform: ${({ rotationAngle }) =>
      `rotate(${rotationAngle}deg) translateY(-4px)`};
  }
`;

const PersonaImageContainer = styled.div`
  position: relative;

  svg {
    position: absolute;
    bottom: 4px;
    right: 4px;
  }
`;

const PersonaImage = styled(SharpPersonaImage)`
  display: flex;
  width: 192px;
  height: 192px;
  height: auto;
  border-radius: ${({ theme }) => theme.shape.corner['0750']};
  transition: border-radius 100ms;
  position: relative;
`;

const Name = styled.p`
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['0680']};

  text-align: center;
`;

const BoostIconContainer = styled.div`
  position: absolute;
  left: -8px;
  top: -8px;
`;

const angles = [2, 4, 6, 8];

const getAngle = (index: number) => {
  const randomItem = angles[Math.floor(Math.random() * angles.length)]!;

  return index % 2 === 0 ? randomItem : -randomItem;
};

export interface ISelectedPersonaProps {
  tokenId: IPersonaFront['tokenId'];
  blockchain: IPersonaFront['blockchain'];
  orderIndex: number;
  isBoosted?: boolean;
}

export const SelectedPersona = ({
  tokenId,
  blockchain,
  isBoosted = false,
  orderIndex,
}: ISelectedPersonaProps) => {
  const t = useTranslations();
  const BcIcon = blockchain === Blockchain.BASE ? BcBaseIcon : BcEthIcon;
  const rotationAngle = useMemo(() => getAngle(orderIndex), [orderIndex]);

  return (
    <PersonaItemContainer rotationAngle={rotationAngle}>
      <PersonaImageContainer>
        <PersonaImage tokenId={tokenId} />
        <BcIcon size="1500" />
      </PersonaImageContainer>
      <Name>{`${t('PERSONA_SHORT')}#${tokenId}`}</Name>
      {isBoosted && (
        <BoostIconContainer>
          <BoostIcon size="1500" />
        </BoostIconContainer>
      )}
    </PersonaItemContainer>
  );
};
